import React from 'react';
import { Badge, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FileEarmarkPdfFill } from 'react-bootstrap-icons';
import { Calendar, Check, CheckCircle, CreditCard, FileX, Plus, Undo2, X } from 'lucide-react';
import moment from 'moment';
import { BILLING_NOTE_ALLOWED_ORDER_STATUSES, CLIENT_CANCELLED_INTERNAL_STATUSES, INVOICE_STATUS_CONFIG } from '../../helpers/constants';
import { formatCurrency, isAdmin } from '../../helpers/global';
import { getInvoiceBalanceDue } from '../reports/utils/helpers';

const getDriveFileViewUrl = fileId => (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null);

const InvoicePdfCell = ({ row }) => {
  const file = row.invoicePdfFile;
  const href = getDriveFileViewUrl(file?.id);
  if (!href) return '—';

  const label = file.fileName || 'Invoice PDF';

  return (
    <div className="d-flex align-items-center justify-content-center" onClick={e => e.stopPropagation()}>
      <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-invoice-pdf-${row._id}`}>{label}</Tooltip>}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="d-inline-flex align-items-center text-decoration-none"
          aria-label={`Download invoice PDF ${label}`}
          onClick={e => e.stopPropagation()}
        >
          <FileEarmarkPdfFill size={16} className="text-danger" />
        </a>
      </OverlayTrigger>
    </div>
  );
};

export const buildStatementServiceUrl = ({ serviceId, orderType }) => {
  const actualServiceId = serviceId?._id || serviceId;
  if (!actualServiceId) return null;
  const params = new URLSearchParams();
  if (orderType) {
    params.set('OrderType', orderType);
  }
  const query = params.toString();
  return `/services/${actualServiceId}${query ? `?${query}` : ''}`;
};

const TruncatedCell = ({ rowId, value, tooltipPrefix }) => {
  if (!value) return '—';

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${tooltipPrefix}-${rowId}`}>{value}</Tooltip>}>
      <span className="d-inline-block text-truncate w-100">{value}</span>
    </OverlayTrigger>
  );
};

const startOfDay = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDaysFromDueDate = dueDate => {
  if (!dueDate) return null;
  const diffMs = startOfDay(dueDate) - startOfDay(new Date());
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const formatDayCount = count => `${count} ${count === 1 ? 'day' : 'days'}`;

export const getStatementRowStyle = (row, { isHovered } = {}) => {
  if (row.status !== 'Overdue') return null;

  return { backgroundColor: isHovered ? '#FFECEC' : '#FFF5F5' };
};

export const formatStatusLabel = (status, dueDate) => {
  if (status === 'Check Sent' || status === 'Cancellation Requested' || status === 'Cancelled') return status;

  const daysDiff = getDaysFromDueDate(dueDate);
  if (daysDiff === null) return status;

  if (status === 'Overdue') {
    const overdueDays = Math.abs(daysDiff);
    return overdueDays === 0 ? 'Overdue today' : `Overdue ${formatDayCount(overdueDays)}`;
  }

  if (status === 'Due') {
    if (daysDiff === 0) return 'Due today';
    if (daysDiff > 0) return `Due in ${formatDayCount(daysDiff)}`;
  }

  return status;
};

const StatusBadge = ({ status, dueDate }) => {
  const cfg = INVOICE_STATUS_CONFIG[status] || INVOICE_STATUS_CONFIG.Due;
  const { Icon } = cfg;
  const label = formatStatusLabel(status, dueDate);
  const style = cfg.style || {};

  return (
    <Badge
      className={`smallFont py-1 px-2 font-weight-semibold rounded-pill d-inline-flex align-items-center`}
      style={style}
    >
      {Icon ? <Icon size={13} className="mr-1 flex-shrink-0" /> : null}
      <span className="text-nowrap">{label}</span>
    </Badge>
  );
};

const ClosingDateCell = ({ value, row, onClosingChangeClick, isAdminUser = false }) => {
  const serviceId = row.serviceId?._id || row.serviceId;
  const formattedDate = value ? moment(value).format('MM/DD/YYYY') : null;

  return (
    <div className="d-flex flex-column align-items-start" onClick={e => e.stopPropagation()}>
      {formattedDate ? (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-closing-date-${row._id}`}>{formattedDate}</Tooltip>}
        >
          <span className="d-inline-block text-truncate">{formattedDate}</span>
        </OverlayTrigger>
      ) : (
        '—'
      )}
      {serviceId && !isAdminUser && (
        <button
          type="button"
          onClick={() => onClosingChangeClick(row)}
          className="tinyFont text-info text-link mt-1 py-0 px-0 border-0 bg-transparent d-inline-flex align-items-center"
          style={{ gap: 4, lineHeight: 1 }}
        >
          <Calendar size={10} className="flex-shrink-0" aria-hidden />
          Change Date
        </button>
      )}
    </div>
  );
};

const BillingNotesCell = ({ row, latestNote, onClick, canAddBillingNote, onBillingNoteAddClick }) => {
  const statementId = row._id;
  const noteText = latestNote?.text?.trim();

  return (
    <div
      className="w-100 d-flex flex-column"
      style={{ minWidth: 0, maxWidth: '100%' }}
      onClick={e => e.stopPropagation()}
    >
      {latestNote && noteText && (
        <>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-billing-${statementId}`}>
                <div>{latestNote.text}</div>
                <small className="text-muted">
                  by {latestNote.author} on {moment(latestNote.createdAt).format('MM/DD/YY')}
                </small>
              </Tooltip>
            }
          >
            <button
              type="button"
              className="tinyFont d-block w-100 text-truncate text-left border-0 bg-transparent p-0 text-dark hover-text-info"
              style={{ minWidth: 0, maxWidth: '100%' }}
              onClick={onClick}
            >
              {latestNote.text}
            </button>
          </OverlayTrigger>
          {latestNote.author && (
            <span className="text-muted tinyFont d-block w-100 text-truncate">
              {latestNote.author} - {moment(latestNote.createdAt).format('MMM D, YYYY')}
            </span>
          )}
        </>
      )}
      <button
        type="button"
        className="d-flex align-items-center flex-shrink-0 xsmallFont bg-transparent border-0 p-0 m-0 text-info text-link"
        onClick={e => {
          e.stopPropagation();
          onBillingNoteAddClick(row);
        }}
        title="Add billing note"
      >
        <Plus size={12} className="text-info " /> Add
      </button>
    </div>
  );
};

const ActionButtons = ({
  row,
  onPay,
  onCheckSent,
  onMarkPaid,
  onFileCancelled,
  onUndoCancellation,
  actionLoading,
  currentUserInfo,
  forceClientView = false,
  payDisabled = false
}) => {
  const isAdminUser = isAdmin(currentUserInfo?.role) && !forceClientView;
  const showClientActions =
    (!isAdminUser || forceClientView) &&
    !(row.isCheckSent || row.status === 'Check Sent' || row.isFileCancelled);
  const showUndoCancellation = row.isFileCancelled;
  const checkKey = `check-${row._id}`;
  const payKey = `pay-${row._id}`;
  const undoKey = `undo-cancellation-${row._id}`;

  const actions = [
    showClientActions && {
      label: 'Pay',
      icon: CreditCard,
      className: 'text-success hover-success-soft',
      loading: !!actionLoading[payKey],
      disabled: payDisabled,
      title: payDisabled ? 'Pay is unavailable while viewing as client' : 'Pay',
      onClick: () => onPay?.(row)
    },
    showClientActions && {
      label: 'Check Sent',
      icon: CheckCircle,
      className: 'text-info hover-info-soft',
      loading: !!actionLoading[checkKey],
      onClick: () => onCheckSent(row)
    },
    showClientActions && {
      label: 'Request Cancellation',
      icon: FileX,
      className: 'text-danger hover-danger-soft',
      onClick: () => onFileCancelled(row)
    },
    showUndoCancellation && {
      label: 'Undo Cancellation',
      icon: Undo2,
      className: 'text-secondary hover-info-soft',
      loading: !!actionLoading[undoKey],
      onClick: () => onUndoCancellation?.(row)
    },
    !showClientActions &&
      isAdminUser && {
        label: 'Mark Paid',
        icon: Check,
        className: 'text-success-dark hover-success-soft',
        onClick: () => onMarkPaid(row)
      }
  ].filter(Boolean);

  return (
    <div className="d-flex flex-nowrap align-items-center" onClick={e => e.stopPropagation()}>
      {actions.map(
        ({ label, icon: Icon, iconClassName, className = '', loading = false, disabled = false, title, onClick }, index) => {
        const baseClassName = `d-flex align-items-center flex-shrink-0 rounded-lg smallFont text-nowrap p-1 ${label &&
          'py-0 px-2 mr-1'}`;

        return (
          <button
            key={index}
            type="button"
            className={`${baseClassName} ${className}`}
            style={{ height: '24px', opacity: disabled ? 0.5 : 1 }}
            disabled={loading || disabled}
            title={title}
            onClick={onClick}
          >
            {loading ? (
              <Spinner size="sm" animation="border" className={label ? 'mr-1' : undefined} />
            ) : Icon ? (
              <Icon
                size={12}
                className={[label ? 'mr-1' : null, iconClassName].filter(Boolean).join(' ') || undefined}
              />
            ) : null}
            {label}
          </button>
        );
      })}
    </div>
  );
};

export const getStatementTableColumns = ({
  onBillingNotesClick,
  onBillingNoteAddClick,
  onClosingChangeClick,
  onPay,
  onCheckSent,
  onMarkPaid,
  onFileCancelled,
  onUndoCancellation,
  actionLoading,
  currentUserInfo,
  forceClientView = false,
  payDisabled = false
}) => {
  const isAdminUser = isAdmin(currentUserInfo?.role) && !forceClientView;

  return [
  ...(isAdminUser
    ? [
        {
          key: 'organizationName',
          label: 'Client',
          sortable: false,
          width: '200px',
          render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="organization" />
        }
      ]
    : []),
  {
    key: 'closingDate',
    label: 'Closing Date',
    sortable: true,
    width: '140px',
    type: 'date',
    style: { whiteSpace: 'normal' },
    render: (value, row) => (
      <ClosingDateCell
        value={value}
        row={row}
        onClosingChangeClick={onClosingChangeClick}
        isAdminUser={isAdminUser}
      />
    )
  },
  {
    key: 'orderType',
    label: 'Service',
    sortable: true,
    width: '100px',
    render: value => {
      const serviceColors = {
        'Lien Search': '#f59e0b',
        Estoppel: '#a855f7',
        Survey: '#10b981'
      };
      const bgColor = serviceColors[value] || 'primary';
      return (
        <div className="d-flex align-items-center">
          <span
            className="rounded-circle flex-shrink-0 mr-2 d-inline-block"
            style={{ width: 8, height: 8, backgroundColor: bgColor }}
          />
          <span>{value || '—'}</span>
        </div>
      );
    }
  },
  {
    key: 'fileNo',
    label: 'File No.',
    sortable: true,
    width: '120px',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="fileno" />
  },
  {
    key: 'propertyAddress',
    label: 'Property Address',
    sortable: true,
    width: '200px',
    render: (_, row) => <TruncatedCell rowId={row._id} value={row.propertyAddress} tooltipPrefix="address" />
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice No.',
    sortable: true,
    align: 'center',
    width: '100px',
    render: (value, row) => {
      return value ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-invoice-number-${row._id}`}>{value}</Tooltip>}>
          <span className="d-inline-block text-truncate w-100">{value}</span>
        </OverlayTrigger>
      ) : (
        '—'
      );
    }
  },
  {
    key: 'invoicePdfFile',
    label: 'Invoice PDF',
    sortable: false,
    align: 'center',
    width: '100px',
    render: (_, row) => <InvoicePdfCell row={row} />
  },
  {
    key: 'invoiceAmount',
    label: 'Invoice Amount',
    sortable: true,
    align: 'center',
    width: '120px',
    type: 'currency',
    fontWeight: '600',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={value != null && value !== '' ? formatCurrency(value) : null}
        tooltipPrefix="invoiceAmount"
      />
    )
  },
  {
    key: 'balanceDue',
    label: 'Balance Due',
    sortable: false,
    align: 'center',
    width: '120px',
    type: 'currency',
    render: (_, row) => {
      const balanceDue = getInvoiceBalanceDue(row);
      const num = Number(balanceDue);
      const displayValue =
        balanceDue == null || balanceDue === '' || Number.isNaN(num) || num <= 0
          ? '—'
          : formatCurrency(num);

      return (
        <TruncatedCell
          rowId={row._id}
          value={displayValue}
          tooltipPrefix="balanceDue"
        />
      );
    }
  },
  {
    key: 'invoiceDueDate',
    label: 'Due Date',
    sortable: true,
    align: 'center',
    width: '120px',
    type: 'date',
    dateFormat: 'MM/DD/YYYY',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={value ? moment(value).format('MM/DD/YYYY') : null}
        tooltipPrefix="dueDate"
      />
    )
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: '200px',
    render: (value, row) => {
      // Client-facing: cancellation request vs final cancelled/refunded file
      if (row.isFileCancelled) {
        const orderStatus = row.orderId?.status;
        const status = CLIENT_CANCELLED_INTERNAL_STATUSES.includes(orderStatus)
          ? 'Cancelled'
          : 'Cancellation Requested';
        return <StatusBadge status={status} dueDate={null} />;
      }
      if (row.isCheckSent || value === 'Check Sent') {
        return <StatusBadge status="Check Sent" dueDate={null} />;
      }
      return <StatusBadge status={value} dueDate={row.invoiceDueDate} />;
    }
  },
  ...(isAdminUser
    ? [
        {
          key: 'checkSentDate',
          label: 'Check Sent Date',
          sortable: true,
          width: '120px',
          dateFormat: 'MM/DD/YYYY',
          render: (value, row) => (
            <TruncatedCell
              rowId={row._id}
              value={value ? moment(value).format('MM/DD/YYYY') : null}
              tooltipPrefix="checkSentDate"
            />
          )
        }
      ]
    : []),
  {
    key: 'billingNotes',
    label: 'Billing Notes',
    sortable: false,
    width: '180px',
    style: { whiteSpace: 'normal', overflow: 'hidden' },
    render: (value, row) => {
      const canAddBillingNote = BILLING_NOTE_ALLOWED_ORDER_STATUSES.includes(row.orderId?.status);
      return (
        <BillingNotesCell
          row={row}
          latestNote={row.latestBillingNote}
          onClick={() => onBillingNotesClick(row)}
          canAddBillingNote={canAddBillingNote}
          onBillingNoteAddClick={onBillingNoteAddClick}
        />
      );
    }
  },
  {
    key: 'actions',
    label: 'Actions',
    sortable: false,
    width: `${!isAdminUser ? '350px' : '300px'}`,
    style: { whiteSpace: 'nowrap', textOverflow: 'clip' },
    render: (_, row) => (
      <ActionButtons
        row={row}
        onPay={onPay}
        onCheckSent={onCheckSent}
        onMarkPaid={onMarkPaid}
        onFileCancelled={onFileCancelled}
        onUndoCancellation={onUndoCancellation}
        actionLoading={actionLoading}
        currentUserInfo={currentUserInfo}
        forceClientView={forceClientView}
        payDisabled={payDisabled}
      />
    )
  }
];
};

export const getReconciliationTableColumns = ({ onReconcileClick, onIgnoreClick }) => [
  {
    key: 'organizationName',
    label: 'Client',
    render: (value, row) => <TruncatedCell rowId={row._id} value={row.organizationName} tooltipPrefix="organization" />,
    width: 200
  },
  {
    key: 'fileNo',
    label: 'File No.',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="fileno" />,
    width: 120
  },
  {
    key: 'propertyAddress',
    label: 'Property Address',
    render: (value, row) => <TruncatedCell rowId={row._id} value={row.propertyAddress} tooltipPrefix="address" />,
    width: 300
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice No.',
    align: 'center',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="invoiceNumber" />,
    width: 130
  },
  {
    key: 'invoiceAmount',
    label: 'Invoice Amount',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.invoiceAmount != null && row.invoiceAmount !== '' ? formatCurrency(row.invoiceAmount) : null}
        tooltipPrefix="invoiceAmount"
      />
    ),
    width: 170
  },
  {
    key: 'hardCost',
    label: 'Hard Cost',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.hardCost != null && row.hardCost !== '' ? formatCurrency(row.hardCost) : null}
        tooltipPrefix="hardCost"
      />
    ),
    width: 140
  },
  {
    key: 'collectedFee',
    label: 'Collected Fee',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.collectedFee != null && row.collectedFee !== '' ? formatCurrency(row.collectedFee) : null}
        tooltipPrefix="collectedFee"
      />
    ),
    width: 150
  },
  {
    key: 'paidDate',
    label: 'Paid Date',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.paidDate ? moment(row.paidDate).format('MM/DD/YYYY') : null}
        tooltipPrefix="paidDate"
      />
    ),
    width: 130
  },
  {
    key: 'actions',
    label: 'Actions',
    sortable: false,
    render: (value, row) => (
      <div className="d-flex flex-nowrap align-items-center" style={{ gap: 6 }} onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="hover-reconcile rounded-lg xsmallFont d-flex align-items-center justify-content-center"
          style={{ height: '24px' }}
          onClick={e => {
            e.stopPropagation();
            onReconcileClick(row._id);
          }}
        >
          <Check size={12} className="flex-shrink-0 mr-1" />
          Reconcile
        </button>
        <button
          type="button"
          className="hover-ignore rounded-lg xsmallFont d-flex align-items-center justify-content-center"
          style={{ height: '24px' }}
          onClick={e => {
            e.stopPropagation();
            onIgnoreClick(row._id);
          }}
        >
          <X size={12} className="flex-shrink-0 mr-1" />
          Ignore
        </button>
      </div>
    ),
    width: 220
  }
];

export const getReconciliationHistoryBatchColumns = () => [
  {
    key: 'organizationName',
    label: 'Client',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="organization" />,
    width: 200
  },
  {
    key: 'fileNo',
    label: 'File No.',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="fileno" />,
    width: 120
  },
  {
    key: 'propertyAddress',
    label: 'Property Address',
    render: (value, row) => <TruncatedCell rowId={row._id} value={row.propertyAddress} tooltipPrefix="address" />,
    width: 300
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice No.',
    align: 'center',
    render: (value, row) => <TruncatedCell rowId={row._id} value={value} tooltipPrefix="invoiceNumber" />,
    width: 150
  },
  {
    key: 'invoiceAmount',
    label: 'Invoice Amount',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.invoiceAmount != null && row.invoiceAmount !== '' ? formatCurrency(row.invoiceAmount) : null}
        tooltipPrefix="invoiceAmount"
      />
    ),
    width: 180
  },
  {
    key: 'hardCost',
    label: 'Hard Cost',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.hardCost != null && row.hardCost !== '' ? formatCurrency(row.hardCost) : null}
        tooltipPrefix="hardCost"
      />
    ),
    width: 130
  },
  {
    key: 'collectedFee',
    label: 'Collected Fee',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.collectedFee != null && row.collectedFee !== '' ? formatCurrency(row.collectedFee) : null}
        tooltipPrefix="collectedFee"
      />
    ),
    width: 165
  },
  {
    key: 'paidDate',
    label: 'Paid Date',
    align: 'left',
    render: (value, row) => (
      <TruncatedCell
        rowId={row._id}
        value={row.paidDate ? moment(row.paidDate).format('MM/DD/YYYY') : null}
        tooltipPrefix="paidDate"
      />
    ),
    width: 130
  }
];
